import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { SectionCard } from '@/components/composites/SectionCard';
import { DailyHoursChart } from '@/components/composites/DailyHoursChart';
import { Badge } from '@/components/primitives/Badge';
import { useAppContext } from '@/context/AppContext';
import {
  useDashboardEmployees,
  useDashboardProjects,
  useDashboardSync,
  useDashboardDailyStats,
} from '@/hooks/useDashboardData';
import styles from './DashboardPage.module.css';

export function DashboardPage() {
  const { dateRange } = useAppContext();
  const { data: employees, isLoading: employeesLoading } = useDashboardEmployees();
  const {
    data: clientGroups,
    totals: projectTotals,
    isLoading: projectsLoading,
  } = useDashboardProjects();
  const { data: syncData, isLoading: syncLoading } = useDashboardSync();
  const { data: dailyStats } = useDashboardDailyStats();

  // Task 156 D3 — drill-down links carry the active date range so detail
  // pages render the same window the user is staring at. The detail
  // pages already accept `?startDate=…&endDate=…`; this is just a
  // pass-through.
  const rangeQs =
    dateRange.start && dateRange.end
      ? `?startDate=${encodeURIComponent(dateRange.start)}&endDate=${encodeURIComponent(dateRange.end)}`
      : '';

  const totalHours = employees.reduce((s, e) => s + e.totalHours, 0);
  const billableHours = employees.reduce((s, e) => s + e.billableHours, 0);
  const memberCount = employees.length;
  // Task 145 D5 — top card #4 mirrors the new "Clients & Projects" section
  // heading; show the client count rather than project count. Internal/Not
  // Set is a real client bucket from the user's perspective (rows whose
  // project has no client roll up here), so it's included in the count.
  const clientCount = clientGroups.length;

  // Task 145 D4 — All client groups expanded by default (mirrors the
  // ManageClientsPage pattern). The state is keyed by `clientId ?? '__null__'`
  // so the Internal / Not Set bucket gets its own entry without colliding.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const g of clientGroups) map[g.clientId ?? '__null__'] = true;
    return map;
  });

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  };

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Total Hours', value: `${totalHours.toFixed(1)}h` },
    { label: 'Billable Hours', value: `${billableHours.toFixed(1)}h` },
    { label: 'Team Members', value: memberCount },
    { label: 'Clients', value: clientCount },
  ];

  // Task 168 — skeleton loaders. Each section reserves layout while its
  // backing query is in flight; sections fill in independently as the
  // three React Query hooks resolve, so a slow endpoint never holds the
  // page hostage.
  const cardsLoading = employeesLoading || projectsLoading;

  return (
    <>
      <PageHeader title="Dashboard" />
      {cardsLoading ? (
        <div className={styles.skeletonStatCardGrid} aria-label="Loading summary cards">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeletonStatCard}>
              <div className={`${styles.skeleton} ${styles.skeletonStatCardLabel}`} />
              <div className={`${styles.skeleton} ${styles.skeletonStatCardValue}`} />
            </div>
          ))}
        </div>
      ) : (
        <StatCardGrid cards={cards} />
      )}

      {/* Task 157 D4 — daily billable vs non-billable hours over the
          active date range. The chart sits above the Team section so
          the page scans top-to-bottom from "what happened" (chart) to
          "who did it" (employees) to "what was it on" (clients/projects). */}
      <div className={styles.chartCardWrap}>
        <SectionCard title="Daily Hours" subtitle="Billable vs non-billable">
          {employeesLoading ? (
            <div className={`${styles.skeleton} ${styles.skeletonChart}`} />
          ) : (
            <DailyHoursChart days={dailyStats} />
          )}
        </SectionCard>
      </div>

      <h2 className={styles.sectionHeader}>Team</h2>
      <div className={styles.memberGrid}>
        {employeesLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`${styles.memberCard} ${styles.skeleton} ${styles.skeletonMemberCard}`}
                aria-label="Loading team member"
              />
            ))
          : employees.map((emp) => (
              <Link
                key={emp.id}
                to={`/employees/${emp.id}${rangeQs}`}
                className={styles.memberCard}
                data-testid="member-card"
              >
                <span className={styles.memberName}>{emp.name}</span>
                <div className={styles.memberEmail}>{emp.email}</div>
                <div className={styles.memberRows}>
                  <div className={styles.memberRow}>
                    <span className={styles.memberRowLabel}>Total</span>
                    <span className={styles.memberRowValue}>{emp.totalHours.toFixed(1)}h</span>
                  </div>
                  <div className={styles.memberRow}>
                    <span className={styles.memberRowLabel}>Billable</span>
                    <span className={`${styles.memberRowValue} ${styles.billableValue}`}>
                      {emp.billableHours.toFixed(1)}h
                    </span>
                  </div>
                  <div className={styles.memberRow}>
                    <span className={styles.memberRowLabel}>General</span>
                    <span className={`${styles.memberRowValue} ${styles.generalValue}`}>
                      {emp.generalHours.toFixed(1)}h
                    </span>
                  </div>
                </div>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${emp.billablePercent}%` }}
                  />
                </div>
                <div className={styles.progressLabel}>
                  <span className={styles.progressPercent}>{emp.billablePercent}% billable</span>
                  <span className={styles.progressEvents}>
                    {emp.events.toLocaleString()} events
                  </span>
                </div>
              </Link>
            ))}
      </div>

      {/* Task 145 D4 — Clients & Projects accordion. Mirrors the visual
          pattern on /clients (ManageClientsPage) but with time-scoped
          Hours / % of Total / Events columns instead of Slug / Created /
          Actions. Inlined rather than extracted to a composite — the two
          pages diverge enough on columns and behaviour (Dashboard is
          read-only) that a shared composite would force a complex
          column-config props API. Follow-up cleanup deferred. */}
      <h2 className={styles.sectionHeader}>Clients &amp; Projects</h2>
      <div className={styles.tableCard} data-testid="clients-projects-accordion">
        <table className={styles.table}>
          <caption className={styles.srOnly}>Clients and projects with hours breakdown</caption>
          <thead>
            <tr>
              <th className={styles.th} style={{ width: '40%' }}>
                Client / Project
              </th>
              <th className={styles.th} style={{ width: '14%' }}>
                Type
              </th>
              <th className={styles.thRight} style={{ width: '14%' }}>
                Hours
              </th>
              <th className={styles.thRight} style={{ width: '16%' }}>
                % of Total
              </th>
              <th className={styles.thRight} style={{ width: '16%' }}>
                Events
              </th>
            </tr>
          </thead>
          {projectsLoading ? (
            <tbody aria-label="Loading clients and projects">
              {Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={5}>
                    <div className={`${styles.skeleton} ${styles.skeletonRow}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          ) : null}
          {!projectsLoading &&
            clientGroups.map((group) => {
              const groupKey = group.clientId ?? '__null__';
              const isOpen = openGroups[groupKey] ?? true;
              return (
                <tbody key={groupKey} data-testid="client-group">
                  {/* Client header row */}
                  <tr
                    className={styles.clientRow}
                    onClick={() => toggleGroup(groupKey)}
                    data-testid="client-row"
                  >
                    <td>
                      <div className={styles.clientNameCell}>
                        <span className={styles.chevronWrap}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{
                              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 200ms ease',
                            }}
                            aria-hidden="true"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </span>
                        {/* Task 156 D3 — clicking the client name navigates
                          to the Client Detail page with the active date
                          range carried over. The Internal / Not Set
                          bucket has clientId === null and renders as
                          plain text instead of a link. */}
                        {group.clientId ? (
                          <Link
                            to={`/clients/${group.clientId}${rangeQs}`}
                            className={styles.clientNameLink}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="client-row-link"
                          >
                            {group.clientName}
                          </Link>
                        ) : (
                          <span className={styles.clientNameLabel}>{group.clientName}</span>
                        )}
                        <span className={styles.projectCount}>
                          {group.projects.length}{' '}
                          {group.projects.length === 1 ? 'project' : 'projects'}
                        </span>
                      </div>
                    </td>
                    <td>{'—'}</td>
                    <td className={`${styles.cellRight} ${styles.mono}`}>
                      {group.rollup.hours.toFixed(1)}
                    </td>
                    <td className={`${styles.cellRight} ${styles.mono}`}>
                      {Math.round(group.rollup.pct)}%
                    </td>
                    <td className={`${styles.cellRight} ${styles.mono}`}>
                      {group.rollup.events.toLocaleString()}
                    </td>
                  </tr>

                  {/* Project rows — visible when the group is expanded. */}
                  {isOpen &&
                    group.projects.map((proj) => (
                      <tr
                        key={`${groupKey}::${proj.name}`}
                        className={styles.projectRow}
                        data-testid="project-row"
                      >
                        <td className={styles.projectNameCell}>{proj.name}</td>
                        <td>
                          <Badge variant={proj.type}>
                            {proj.type === 'billable' ? 'Billable' : 'General'}
                          </Badge>
                        </td>
                        <td className={`${styles.cellRight} ${styles.mono}`}>
                          {proj.hours.toFixed(1)}
                        </td>
                        <td className={`${styles.cellRight} ${styles.mono}`}>
                          {/* Task 113 D4 — whole-number percent. */}
                          {Math.round(proj.pct)}%
                        </td>
                        <td className={`${styles.cellRight} ${styles.mono}`}>
                          {proj.events.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                </tbody>
              );
            })}
          <tfoot>
            <tr className={styles.totalsRow}>
              <td>
                <strong>Total</strong>
              </td>
              <td />
              <td className={`${styles.cellRight} ${styles.mono}`}>
                {projectTotals.hours.toFixed(1)}
              </td>
              <td className={`${styles.cellRight} ${styles.mono}`}>100%</td>
              <td className={`${styles.cellRight} ${styles.mono}`}>
                {projectTotals.events.toLocaleString()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <h2 className={styles.sectionHeader}>Data Pipeline</h2>
      <div className={styles.syncBar}>
        <span className={styles.syncDot} aria-hidden="true" />
        <span className={styles.syncBadge}>{syncLoading ? 'Loading…' : syncData.status}</span>
        <span className={styles.syncTime} data-testid="updated-ago">
          {syncLoading ? '' : syncData.lastSyncRelative}
        </span>
        <div className={styles.syncSpacer} />
        <button type="button" className={styles.syncBtn}>
          Sync now
        </button>
      </div>
    </>
  );
}
